import { AuthService } from "./auth.service";
import {
  getAuthSession,
  getAuthToken,
  setAuthSession,
} from "../pages/AdminAuth/components/authStore";

function cleanId(value) {
  return String(value || "").trim();
}

function readTokenPayload() {
  try {
    const token = getAuthToken();
    const part = String(token || "").split(".")[1];
    if (!part) return {};

    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const json = decodeURIComponent(
      Array.from(atob(padded))
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join("")
    );
    return JSON.parse(json) || {};
  } catch (_err) {
    return {};
  }
}

/**
 * Ensure the browser holds a warehouse-scoped auth token before opening
 * warehouse operational pages. Updating local session alone is not enough:
 * all-warehouse/super-admin tokens still require a selected warehouse context
 * on the backend.
 */
export async function ensureWarehouseContext(warehouseId) {
  const requestedWarehouseId = cleanId(warehouseId);
  const session = getAuthSession() || {};
  const currentWarehouseId = cleanId(session?.selectedWarehouseId);
  const tokenPayload = readTokenPayload();
  const tokenWarehouseId = cleanId(tokenPayload?.selectedWarehouseId);
  const tokenDashboard = String(tokenPayload?.currentDashboard || "").toLowerCase();

  // Trust the signed token, not only localStorage. The old redirect bug could leave
  // localStorage saying "warehouse" while the JWT was still an all-warehouse
  // admin token with no selected warehouse.
  if (
    requestedWarehouseId &&
    tokenDashboard === "warehouse" &&
    tokenWarehouseId === requestedWarehouseId
  ) {
    return {
      switched: false,
      selectedWarehouseId: tokenWarehouseId,
    };
  }

  // A warehouse operational route always needs an explicit warehouse when the
  // current token has no selected context (notably super-admin mode=all).
  const finalWarehouseId = requestedWarehouseId || currentWarehouseId;
  if (!finalWarehouseId) {
    const error = new Error("Warehouse context is missing for this action.");
    error.code = "WAREHOUSE_CONTEXT_REQUIRED";
    throw error;
  }

  const res = await AuthService.switchContext({
    dashboard: "warehouse",
    warehouseId: finalWarehouseId,
  });

  setAuthSession({
    accessToken: res?.accessToken,
    admin: res?.admin,
    dashboardAccess: res?.dashboardAccess,
    warehouseAccess: res?.warehouseAccess,
    currentDashboard: res?.currentDashboard || "warehouse",
    selectedWarehouseId: res?.selectedWarehouseId || finalWarehouseId,
  });

  return {
    switched: true,
    selectedWarehouseId: res?.selectedWarehouseId || finalWarehouseId,
    response: res,
  };
}
